import { useNavigate } from "react-router-dom";
import { navigateTo, showToastMessage } from "../../../utils/commonFunctions"
import useUrlParams from "./useUrlParams"
import { isOperateMode } from "./utils"
import { useContext, useCallback } from "react"
import { CommandCenterStateProviderContext } from "../context"
import { SCENARIO_DEPLOYMENT_TYPES } from "../../simulate/constants"
import useTelemetryConfirm from "../components/center/telemetry/hooks/useTelemetryConfirm";
import { getTelemetryStorageKey, sanitizeWorkspaceForBackend } from "../components/center/telemetry/lib/telemetry-helpers";
import TelemetryService from "../../../service/TelemetryService";
import { getApiFailureMessage } from "../../../utils/commonFunctions";

const useUrlNavigation = () => {

    const navigate = useNavigate();
    const { mission_name, mission_id, dashboard, scenario_id, tab_name, sat_grp_id, sat_id } = useUrlParams()
    const context = useContext(CommandCenterStateProviderContext)
    const scenario = context?.scenario ?? null
    const { confirmUnsavedChanges } = useTelemetryConfirm();

    const saveWorkspaceDirectly = useCallback(async () => {
        const deployDetails = context?.scenarioDeploymentDetails;
        const selectedSatellite = deployDetails?.satellites?.find((sat) => sat.satelliteId === sat_id);
        const activeTrueTwinId = selectedSatellite?.trueTwinId || deployDetails?.satellites?.[0]?.trueTwinId || (context as any)?.truetwin_id;
        const telemetryDashboards = context?.telemetryDashboards;
        const dashboards = telemetryDashboards?.workspaces || [];

        if (activeTrueTwinId) {
            const sanitizedWorkspaces = sanitizeWorkspaceForBackend(dashboards);
            const payload = {
                dashboardType: telemetryDashboards?.dashboardType || 'SIMULATE',
                workspaces: sanitizedWorkspaces,
            };
            try {
                await TelemetryService.saveDashboardConfig(activeTrueTwinId, payload);
                showToastMessage('success', 'Telemetry dashboard saved successfully');
                const uniqueId = telemetryDashboards?.uniqueId || 'telemetry-config';
                const storageKey = getTelemetryStorageKey(uniqueId, activeTrueTwinId);
                try {
                    sessionStorage.setItem(storageKey, JSON.stringify(dashboards));
                } catch (_) { }
                context?.setSavedTelemetryDashboards?.(telemetryDashboards ?? null);
                context?.setHasUnsavedChanges?.(false);
            } catch (err: any) {
                const msg = getApiFailureMessage(err, 'Update');
                showToastMessage('error', msg ?? 'Failed to save telemetry dashboard');
                throw err;
            }
        } else {
            context?.setHasUnsavedChanges?.(false);
        }
    }, [context, sat_id]);

    const discardWorkspaceDirectly = useCallback(() => {
        const deployDetails = context?.scenarioDeploymentDetails;
        const selectedSatellite = deployDetails?.satellites?.find((sat) => sat.satelliteId === sat_id);
        const activeTrueTwinId = selectedSatellite?.trueTwinId || deployDetails?.satellites?.[0]?.trueTwinId || (context as any)?.truetwin_id;
        const savedTelemetryDashboards = context?.savedTelemetryDashboards;

        if (savedTelemetryDashboards) {
            context?.setTelemetryDashboards?.(savedTelemetryDashboards);
            if (activeTrueTwinId) {
                const uniqueId = savedTelemetryDashboards.uniqueId || 'telemetry-config';
                const storageKey = getTelemetryStorageKey(uniqueId, activeTrueTwinId);
                try {
                    if (savedTelemetryDashboards.workspaces && savedTelemetryDashboards.workspaces.length > 0) {
                        sessionStorage.setItem(storageKey, JSON.stringify(savedTelemetryDashboards.workspaces));
                    } else {
                        sessionStorage.removeItem(storageKey);
                    }
                } catch (_) { }
            }
        }
        context?.setHasUnsavedChanges?.(false);
    }, [context, sat_id]);

    const isTelemetryTab = tab_name === 'monitor';

    const withUnsavedCheck = (proceed: () => void) => {
        // Only prompt for unsaved telemetry changes when actively leaving the telemetry (monitor) tab
        if (isTelemetryTab && context?.hasUnsavedChanges) {
            confirmUnsavedChanges(
                async () => {
                    try {
                        await saveWorkspaceDirectly();
                        proceed();
                    } catch (_) {
                        showToastMessage('error', 'Failed to save telemetry dashboard');
                    }
                },
                () => {
                    discardWorkspaceDirectly();
                    proceed();
                }
            );
            return;
        }
        proceed();
    };

    const navigate_to = (tab: string) => {
        const search_params = window.location.search ? `${window.location.search}` : "";
        withUnsavedCheck(() => {
            navigateTo(`${mission_name}/${mission_id}/${dashboard}/${scenario_id}/satGroupId/${sat_grp_id}/satellite/${sat_id}/${tab}${search_params}`, navigate);
        });
    };

    const update_command_center_url = (satGroupId: string, satelliteId: string) => {
        const search_params = window.location.search ? `${window.location.search}` : ""
        withUnsavedCheck(() => {
            navigate(`${process.env.PUBLIC_URL}/${mission_name}/${mission_id}/${dashboard}/${scenario_id}/satGroupId/${satGroupId}/satellite/${satelliteId}/${tab_name}${search_params}`)
        });
    }

    const navigate_to_simulate = () => {
        withUnsavedCheck(() => {
            navigateTo(
                `${mission_name}/${mission_id}/simulate`,
                navigate
            );
        });
    }
    const navigate_to_operate_dashboard = () => {
        withUnsavedCheck(() => {
            navigateTo(
                `${mission_name}/${mission_id}/operate-dashboard`,
                navigate
            );
        });
    }

    const navigate_to_validate = () => {
        withUnsavedCheck(() => {
            navigateTo(`${mission_name}/${mission_id}/validate`, navigate);
        });
    }

    const navigate_home = () => {
        withUnsavedCheck(() => {
            navigateTo(``, navigate);
        });
    }

    const navigate_dashboard = () => {
        withUnsavedCheck(() => {
            const operateMode = isOperateMode(dashboard, scenario)
            const deploymentType = scenario?.scenarioDeploymentType?.toUpperCase()
            if (deploymentType === SCENARIO_DEPLOYMENT_TYPES.VALIDATE) {
                navigateTo(`${mission_name}/${mission_id}/validate`, navigate);
            } else if (deploymentType === SCENARIO_DEPLOYMENT_TYPES.TRUETWIN_COMMAND_CENTER) {
                navigateTo(`${mission_name}/${mission_id}/simulate`, navigate);
            } else if (operateMode && dashboard === 'operate') {
                navigateTo(`${mission_name}/${mission_id}/operate-dashboard`, navigate);
            } else {
                navigateTo(`${mission_name}/${mission_id}/simulate`, navigate);
            }
        });
    }

    return { navigate_to, navigate_dashboard, update_command_center_url, navigate_home }
}
export default useUrlNavigation